import * as React from 'react'
import { cva, type VariantProps } from 'class-variance-authority'
import { cn } from '@/utils/cn'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/UI/Popover'
import { Command, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem } from '@/components/UI/Command'

const selectTriggerVariants = cva(
  'flex w-full items-center justify-between gap-2 px-4 text-body-md text-primary border-input transition-200',
  {
    variants: {
      size: {
        sm: 'h-6 rounded-2',
        md: 'h-8 rounded-2.5',
        lg: 'h-10 rounded-3',
        lgx: 'h-12 rounded-3.5',
      },
    },
    defaultVariants: {
      size: 'md',
    },
  }
)

export interface SelectOption {
  value: string
  label: string
}

export interface SelectWithSearchProps {
  id?: string
  options: SelectOption[]
  value: string
  onValueChange: (value: string) => void
  placeholder?: string
  searchPlaceholder?: string
  emptyText?: string
  disabled?: boolean
  size?: VariantProps<typeof selectTriggerVariants>['size']
  className?: string
  triggerClassName?: string
}

const SelectWithSearch = React.forwardRef<HTMLButtonElement, SelectWithSearchProps>(
  ({
    id,
    options,
    value,
    onValueChange,
    placeholder = '请选择',
    searchPlaceholder = '搜索...',
    emptyText = '无匹配结果',
    disabled = false,
    size = 'lg',
    className,
    triggerClassName,
  }, ref) => {
    const [open, setOpen] = React.useState(false)
    const [searchValue, setSearchValue] = React.useState('')

    const selectedOption = React.useMemo(
      () => options.find(option => option.value === value),
      [options, value]
    )

    const filteredOptions = React.useMemo(() => {
      if (!searchValue) return options
      const searchLower = searchValue.toLowerCase()
      return options.filter(option =>
        option.label.toLowerCase().includes(searchLower)
      )
    }, [options, searchValue])

    const handleSelect = (selectedValue: string) => {
      onValueChange(selectedValue)
      setOpen(false)
      setSearchValue('')
    }

    return (
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            ref={ref}
            id={id}
            type="button"
            disabled={disabled}
            className={cn(
              selectTriggerVariants({ size }),
              'text-left cursor-pointer focus:outline-hidden [&>iconpark-icon]:color-brand-primary',
              disabled && 'cursor-not-allowed ![&>iconpark-icon]:text-disabled',
              triggerClassName
            )}
          >
            <span className={cn(!selectedOption && 'text-tertiary')}>
              {selectedOption?.label || placeholder}
            </span>
            <iconpark-icon name="outline-down-2px" size="14" />
          </button>
        </PopoverTrigger>
        <PopoverContent
          className="w-[var(--radix-popover-trigger-width)] bg-surface bor-2 rounded-3 shadow-lg z-50"
          align="start"
        >
          <Command className='py-2'>
            <CommandInput
              value={searchValue}
              onValueChange={setSearchValue}
              placeholder={searchPlaceholder}
              className="h-10 focus:outline-none"
            />
            <CommandList className='max-h-[300px]'>
              <CommandEmpty>{emptyText}</CommandEmpty>
              <CommandGroup>
                {filteredOptions.map(option => (
                  <CommandItem
                    key={option.value}
                    value={option.label}
                    onSelect={() => handleSelect(option.value)}
                    className={cn(
                      'cursor-pointer hover:bg-overlay-1 focus:bg-overlay-1',
                      value === option.value && 'bg-overlay-1'
                    )}
                  >
                    <span className="flex-1">{option.label}</span>
                    {value === option.value && (
                      <iconpark-icon name="outline-check-2px" size="14" className="text-brand-primary" />
                    )}
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    )
  }
)

SelectWithSearch.displayName = 'SelectWithSearch'

export { SelectWithSearch }
